import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NotificationService, AppNotification } from '../notification.service';

@Component({
  selector: 'app-notification-list',
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.css']
})
export class NotificationListComponent implements OnInit, OnDestroy {
  selectedIds: Set<string> = new Set();
  private sub!: Subscription;

  constructor(
    public notifService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.notifService.loadNotifications();
    // Reset selection whenever the notification list changes
    this.sub = this.notifService.notifications$.subscribe(() => {
      // Prune selected IDs that no longer exist
      const currentIds = new Set(
        this.notifService.groupedNotifications.flatMap(g => g.items.map(i => i.id))
      );
      this.selectedIds.forEach(id => {
        if (!currentIds.has(id)) this.selectedIds.delete(id);
      });
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  toggleSelect(id: string): void {
    const next = new Set(this.selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.selectedIds = next;
  }

  markSelectedAsRead(): void {
    if (this.selectedIds.size === 0) return;
    this.notifService.markAsRead(Array.from(this.selectedIds));
    this.selectedIds = new Set();
  }

  deleteSelected(): void {
    if (this.selectedIds.size === 0) return;
    this.notifService.deleteNotifications(Array.from(this.selectedIds));
    this.selectedIds = new Set();
  }

  navigateToNotification(notif: AppNotification): void {
    // Mark as read when clicked
    if (!notif.isRead) {
      this.notifService.markAsRead([notif.id]);
    }

    if (notif.navigation && notif.navigation.route_name) {
      const routeName = notif.navigation.route_name;
      const params = notif.navigation.params || {};

      // FIX 3: Route to specific offer/campaign view using the entity ID
      const routePath = this.getRoutePathFromName(routeName, params);

      // If we have a specific entity ID (highlight_ids[0] or id param), navigate directly to the view screen
      const entityId = params['highlight_ids']?.[0] || params['id'] || params['offer_id'] || params['campaign_id'] || null;

      if (entityId) {
        // Navigate to the specific detail/view route: e.g. /admin/marketing-offers/view/123
        this.router.navigate([routePath, 'view', entityId]);
      } else if (params['highlight_ids'] && params['highlight_ids'].length > 0) {
        this.router.navigate([routePath], {
          queryParams: { highlightIds: params['highlight_ids'].join(',') }
        });
      } else {
        this.router.navigate([routePath], {
          queryParams: params
        });
      }
    }
  }

  /**
   * Map notification route_name to application route path
   * Add more mappings as needed based on your routes
   */
  private getRoutePathFromName(routeName: string, params?: any): string {
    const routeMap: { [key: string]: string } = {
      'campaign_approval': '/admin/marketing-offers',
      'offer_approval': '/admin/marketing-offers',
      'marketing_offers': '/admin/marketing-offers',
      'lexiz_lawyers': '/admin/lexiz-lawyers',
      'lexiz_clients': '/admin/lexiz-clients',
      'lexiz_corporate': '/admin/lexiz-corporate',
      'members': '/groups/view-member',
      'leads': '/leads',
      'matters': '/matter',
      'relationships': '/relationship',
      'documents': '/document/view/firm',
      'notifications': '/notifications',
      'audit_trails': '/audit-trails'
    };

    return routeMap[routeName] || '/';
  }
}